/**
 * SiteSwitcher.tsx — the workspace switcher pattern used by Linear/Notion/
 * Slack: a dropdown under the brand mark showing the current site, every
 * other site one click away, and a "Manage sites" link into the full
 * Sites settings screen. This is the answer to "I can't see to add more
 * sites" — previously nothing in the UI exposed more than the one
 * hardcoded site at all.
 */
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, Globe, Plus, Settings } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function SiteSwitcher() {
  const { sites, currentSite, currentSiteId, setCurrentSiteId, isLoading } = useSite();
  const { can } = useAuth();
  // Item 7's granular RBAC: "Manage sites" reflects the real
  // "sites.manage" capability, not a hardcoded manager check — a manager
  // has it unconditionally, so this is a strict generalization.
  const canManageSites = can("sites.manage");
  const navigate = useNavigate();

  if (isLoading) {
    return <div className="mx-3 h-11 animate-pulse rounded-md bg-neutral-100" />;
  }

  if (sites.length === 0) {
    // No sites exist yet at all (shouldn't happen once seeded, but a fresh
    // DB with the seed skipped could land here) — go straight to creating one.
    return (
      <button
        onClick={() => navigate("/settings/sites")}
        className="mx-3 flex items-center gap-2 rounded-md border border-dashed border-neutral-300 px-2.5 py-2 text-sm text-neutral-500 hover:border-neutral-400 hover:text-neutral-700"
      >
        <Plus className="size-4" />
        Add your first site
      </button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="mx-3 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-accent-500/50">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-neutral-900 text-sm font-bold text-white">
          {(currentSite?.name ?? "?").charAt(0).toUpperCase()}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-neutral-900">
            {currentSite?.name ?? "Select a site"}
          </span>
          <span className="block truncate text-xs text-neutral-400">{currentSite?.domain}</span>
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-neutral-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Sites</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sites.map((site) => (
          <DropdownMenuItem key={site.id} onSelect={() => setCurrentSiteId(site.id)}>
            <Globe className="size-4 text-neutral-400" />
            <span className="min-w-0 flex-1 truncate">{site.name}</span>
            {site.id === currentSiteId && <Check className="size-4 text-accent-600" />}
          </DropdownMenuItem>
        ))}
        {canManageSites && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/settings/sites")}>
              <Settings className="size-4" />
              Manage sites
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
